import React from "react";
import type { ApolloClient } from "@apollo/client";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { push } from "store/actions/router";
import { getUserState } from "actions/userActions";
import { INDIVIDUAL_WAITING_APPROVAL } from "statics/onboardingSteps";
import { Auth0Context } from "utils/react-auth0-wrapper";
import { scrollToTop } from "utils/dom";
import { getAllAccounts } from "actions/accountActions";
import {
  activeAccountState,
  isOnboardingSubsequentAccountSelector,
  isRolloverSelector,
  userIs401kRollIn,
} from "store/selectors/user";
import { Card } from "react-bootstrap";
import { MdPendingActions } from "react-icons/md";
import IconMessageCard from "components/IconMessageCard";

import ProgressBar from "components/ProgressBar";
import Alert from "components/Alert";
import PromisePoller from "components/PromisePoller";
import type { RootState } from "types/state";

import "./IndividualThanks.scss";

const mapStateToProps = (state: RootState, ownProps: OwnProps) => {
  // userState slice is typed `object`; `.state` is the established access path
  const userState = (state.user.userState as { state?: string }).state;
  // `error` is reached via the slice index signature (typed `unknown`)
  const error = state.user.error as string | undefined;
  return {
    userState,
    ownProps,
    error,
    userId: state.user.id,
    isRolloverAccount: isRolloverSelector(state),
    isSubsequentAccount: isOnboardingSubsequentAccountSelector(state),
    accountState: activeAccountState(state),
    isPaychex: userIs401kRollIn(state),
  };
};
const mapDispatchToProps = {
  getUserState,
  getAllAccounts,
  push,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface OwnProps {}

type Props = OwnProps & WithApolloClient<PropsFromRedux>;

interface State {
  approved: boolean;
  rolloverApproved: boolean;
}

class IndividualThanks extends React.Component<Props, State> {
  static contextType = Auth0Context;
  declare context: React.ContextType<typeof Auth0Context>;

  constructor(props: Props) {
    super(props);

    this.state = {
      approved: false,
      rolloverApproved: false,
    };
  }

  // The application under review belongs to an account, not the user. Everyone
  // here already has a user state, so this has to be asked before it, not after.
  get onboardingOffAccount() {
    return this.props.isRolloverAccount || this.props.isSubsequentAccount;
  }

  componentDidMount() {
    window.analytics.page("Individual Thanks");
    scrollToTop();

    if (this.onboardingOffAccount) {
      if (this.props.accountState === "AccountApplicationApproved") {
        this.setState({
          rolloverApproved: true,
        });
      }
    } else if (this.props.userState) {
      if (this.props.userState === "IndividualApplicationApproved") {
        this._markApproved();
      }
    }
  }

  _markApproved = () => {
    window.analytics.track("Individual Application Approved");
    this.setState({ approved: true });
  };

  componentDidUpdate() {
    if (this.onboardingOffAccount) {
      if (
        this.props.accountState === "AccountApplicationApproved" &&
        !this.state.rolloverApproved
      ) {
        this.setState({
          rolloverApproved: true,
        });
      }
      window.analytics.track("Submitted Individual Rollover Application", {
        accountState: this.props.accountState,
      });
    } else if (this.props.userState) {
      if (
        this.props.userState === "IndividualApplicationApproved" &&
        !this.state.approved
      ) {
        this._markApproved();
      }
      window.analytics.track("Submitted Individual Application", {
        userState: this.props.userState,
      });
    }
  }
  verify = async () => {
    try {
      const { getUser } = this.context!;
      const user = await getUser();

      if (this.onboardingOffAccount) {
        // PRESERVED BUG (conversion-only): the dispatch-bound getAllAccounts
        // accepts only (client); the extra userId arg is silently ignored at
        // runtime. Cast to a 2-arg signature to keep the call verbatim.
        const getAllAccounts = this.props.getAllAccounts as (
          client: ApolloClient<object>,
          userId: string | null
        ) => unknown;
        return getAllAccounts(
          this.props.client as ApolloClient<object>,
          this.props.userId
        );
      }

      return this.props.getUserState(
        this.props.client as ApolloClient<object>,
        user
      );
    } catch (e) {
      console.log("Polling Error: " + String(e));
    }
  };

  render() {
    if (this.state.approved) {
      return this.renderFinalizing();
    } else if (this.state.rolloverApproved) {
      window.analytics.track("Individual Rollover Application Approved");
      return this.renderApproved();
    }
    return this.renderChecking();
  }

  renderChecking = () => {
    const title = this.props.isPaychex
      ? "We're finalizing your account now."
      : "Thank you!";
    const subtext = this.props.isPaychex
      ? "It won’t take long—we’ll email you as soon as it’s ready."
      : "Your information has been submitted and we're in the process of setting up your account. This could take up to two business days. We'll email you when it's ready to go.";

    return (
      <div className="individual-thanks">
        <ProgressBar
          isEmployer={false}
          activeStepId={INDIVIDUAL_WAITING_APPROVAL.id}
          progressPercent={"100"}
        />
        <PromisePoller
          key="checking"
          minutesToPollFor={10}
          promiseToPoll={this.verify}
          interval={4000}
        />

        <div className="individual-thanks-container">
          <Card>
            <IconMessageCard
              header={title}
              subheader={subtext}
              icon={<MdPendingActions color="60A4BF" size={16} />}
            />
          </Card>
        </div>
      </div>
    );
  };

  // Holds here until the icon-api-service workflow moves the user onto the
  // lifecycle state machine; PrivateRoute routes off it from there.
  renderFinalizing = () => {
    return (
      <div className="individual-thanks">
        <ProgressBar
          isEmployer={false}
          activeStepId={INDIVIDUAL_WAITING_APPROVAL.id}
          progressPercent={"100"}
        />
        {/* keyed apart from the checking poller so the window restarts here */}
        <PromisePoller
          key="finalizing"
          minutesToPollFor={10}
          promiseToPoll={this.verify}
          interval={4000}
        />

        <div className="individual-thanks-container">
          <Card>
            <IconMessageCard
              header={"We're finalizing your account details."}
              subheader={
                "Hang tight—we'll take you to your account as soon as it's ready."
              }
              icon={<MdPendingActions color="60A4BF" size={16} />}
            />
          </Card>
        </div>
      </div>
    );
  };

  _goToDashboard = () => {
    this.props.push("/dashboard");
  };

  renderApproved = () => {
    const subtext = this.props.accountState
      ? "Your Icon rollover account is now set up and ready for you to finalize."
      : "Your Icon account is now set up and ready for you to finalize.";
    return (
      <>
        {this.props.error && <Alert type="error" msg={this.props.error} />}
        <div className="individual-thanks-container">
          <Card>
            <IconMessageCard
              header={"Success!"}
              subheader={subtext}
              circle={false}
              actionText={"Continue"}
              onClick={this._goToDashboard}
            />
          </Card>
        </div>
      </>
    );
  };
}

export default connector(withApollo<Props>(IndividualThanks));
